import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { get } from 'lodash';
import classNames from 'classnames';
import { Grid } from '@mui/material';
import { GuideDataType } from '../Gude.types';
import { elementBeanListType } from '../../../Flo.types';
import { getImageDimensionsFromUrl } from '../../../../../Common/Common.utils';
import styles from './Instruction.module.css';
import parentStyles from '../../../Flo.module.css';
import { useSelector } from 'react-redux';

interface SlideShowProps {
	steps: GuideDataType[];
	floId: string;
	floIndex: number;
	themeColor: string;
	floElements: Record<string, any>;
	stepsFloElements: Record<string, any>;
	wrapper?: string;
	setRenderStatus?: (index: number) => void;
	hideHeader?: boolean;
	hideHeaderBecauseOfData?: boolean;
	style?: React.CSSProperties;
	internalArticles?: boolean;
	showPoweredBy?: boolean;
	onPoweredByClick?: () => void;
	view?: string;
	StepDetailsComponent: React.ComponentType<any>;
}

const SlideShow: React.FC<SlideShowProps> = ({
	steps,
	floId,
	floIndex,
	themeColor,
	floElements,
	stepsFloElements,
	wrapper,
	setRenderStatus,
	hideHeader = false,
	hideHeaderBecauseOfData = false,
	style = {},
	internalArticles = false,
	showPoweredBy = true,
	onPoweredByClick,
	view,
	StepDetailsComponent,
}) => {
	const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
	const [slideHeight, setSlideHeight] = useState(41);
	const [containerSize, setContainerSize] = useState({
		width: 0,
		height: 0,
		aspectRatio: 0,
	});
	const [childElement, setChildElement] = useState<HTMLElement | null>(null);
	const isEmbed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["is-embed"]`, false)
	);
	const imageURLForDimension: string = useMemo(() => {
		let filteredStep = steps.find(
			(step: GuideDataType) =>
				get(step, 'resourceDetail.resourceType') ===
					'MOUSE_CLICK_IMAGE_CAPTURE_PUBLISHED' &&
				get(step, 'resourceDetail.resourceProperties.elementId', null) !== null
		);
		if (!filteredStep) {
			filteredStep = steps.find(
				(step: GuideDataType) =>
					get(step, 'resourceDetail.resourceType') ===
						'MOUSE_CLICK_IMAGE_CAPTURE_PUBLISHED' &&
					get(step, 'resourceDetail.resourceProperties.screenshotClickId', null) !== null
			);
		}
		return get(filteredStep, 'resourceDetail.url', '');
	}, [steps]);

	// Function to calculate dimensions based on imageURLForDimension
	const calculateDimensions = useCallback(async () => {
		if (!steps.length || !imageURLForDimension) return;

		try {
			const dimensions = await getImageDimensionsFromUrl(imageURLForDimension);
			console.log('dimensions', dimensions);
			if (!dimensions) return;

			console.log('containerSize img dimensions', dimensions);

			// Get viewport dimensions
			const viewportHeight = window.innerHeight - 200;
			const viewportWidth = window.innerWidth;

			// Account for padding, header, footer, and controls
			const paddingVertical = 80;
			const paddingHorizontal = 30;
			const controlsHeight = 32;
			const headerHeight = 60;

			// Calculate available space
			const availableHeight =
				viewportHeight - paddingVertical - controlsHeight - headerHeight;

			// Limit width to 90% of viewport width maximum
			const maxAllowedWidth = Math.min(
				viewportWidth * 0.9,
				viewportWidth - paddingHorizontal
			);
			const availableWidth = maxAllowedWidth;

			// Determine aspect ratio
			const aspectRatio = dimensions.width / dimensions.height;

			// Calculate container dimensions to fit within viewport
			let containerWidth, containerHeight;

			// Check which dimension is the limiting factor
			if (availableWidth / aspectRatio <= availableHeight) {
				// Width is the limiting factor
				containerWidth = availableWidth;
				containerHeight = availableWidth / aspectRatio;
			} else {
				// Height is the limiting factor
				containerHeight = availableHeight;
				containerWidth = Math.min(availableHeight * aspectRatio, availableWidth);
			}

			// Ensure dimensions don't exceed available space
			containerWidth = Math.min(containerWidth, availableWidth);
			containerHeight = Math.min(containerHeight, availableHeight);

			// Set minimum dimensions to ensure usability
			containerWidth = Math.max(containerWidth, 320);
			containerHeight = Math.max(containerHeight, 240);

			const wrapperHeight = wrapper ? 32 : 0;

			// Set the container size
			console.log(
				'containerSize',
				containerSize,
				containerWidth,
				containerHeight + 120 + wrapperHeight
			);
			setContainerSize({
				width: containerWidth,
				height: containerHeight + 120 + wrapperHeight,
				aspectRatio: aspectRatio,
			});
		} catch (error) {
			console.error('Error calculating dimensions from imageURLForDimension:', error);
			// Fallback to default dimensions
			setContainerSize({
				width: 640,
				height: 480,
				aspectRatio: 1.33,
			});
		}
	}, [imageURLForDimension, steps, setContainerSize]);

	// Window resize handler
	useEffect(() => {
		const handleResize = () => {
			calculateDimensions();
		};

		// Add resize event listener
		window.addEventListener('resize', handleResize);

		// Initial calculation
		calculateDimensions();

		// Cleanup
		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, [calculateDimensions]);

	const handleChildRefReady = useCallback((element: HTMLElement | null) => {
		setChildElement(element);
	}, []);

	const currentSlide = steps[currentSlideIndex];

	const handleNext = () => {
		setCurrentSlideIndex((prevIndex) => Math.min(prevIndex + 1, steps.length - 1));
	};

	const handlePrevious = () => {
		setCurrentSlideIndex((prevIndex) => Math.max(prevIndex - 1, 0));
	};

	const handleStepChange = (e: any) => {
		setCurrentSlideIndex(parseInt(e.target.value) - 1);
	};

	const spotlights = get(
		stepsFloElements,
		`${get(currentSlide, 'stepId', '')}.spotlight`,
		[]
	);
	const texts = get(stepsFloElements, `${get(currentSlide, 'stepId', '')}.text`, []);
	const blursList = get(stepsFloElements, `${get(currentSlide, 'stepId', '')}.blur`, []);
	const imageList = get(stepsFloElements, `${currentSlide?.stepId}.image`, []);

	return (
		<Grid
			item
			desktop={12}
			laptop={12}
			tablet={12}
			mobile={12}
			style={style}
			classes={{
				root: classNames(
					parentStyles.leftGridContainer,
					styles.instructions,
					styles.container,
					{
						[parentStyles.gridWithoutHeader]: hideHeader,
						[parentStyles.leftGridContainerWithWebsiteHeader]: internalArticles,
						[styles.withHeader]: !hideHeader && !hideHeaderBecauseOfData,
						[styles.withWebsiteHeader]: internalArticles,
						[styles.presentationContainer]: true,
					}
				),
			}}
		>
			<div
				className={classNames(styles['instruction-content'], styles.instructionContent, {
					[styles.presentationContent]: true,
				})}
				style={{
					overflow: 'auto',
				}}
				ref={(el) => {
					if (el) {
						el.style.setProperty('overflow-y', 'auto', 'important');
					}
				}}
			>
				<ul
					className={classNames(
						styles['instruction-list'],
						'grey-color',
						'font-15',
						styles.instructionList,
						{
							[styles.presentationList]: true,
						}
					)}
				>
					{steps.length > 0 && currentSlide && (
						<StepDetailsComponent
							index={currentSlideIndex}
							element={get(floElements, currentSlide?.elementId)}
							step={currentSlide}
							key={`${get(currentSlide, 'id', '')}_${spotlights.length}_${
								blursList.length
							}_${imageList.length}`}
							blursList={blursList}
							imagesList={imageList}
							spotlights={spotlights}
							texts={texts}
							setRenderStatus={setRenderStatus}
							floId={floId}
							floIndex={floIndex}
							isSlideShow={true}
							handleNext={handleNext}
							handlePrevious={handlePrevious}
							handleStepChange={handleStepChange}
							totalSlides={steps.length}
							showPoweredBy={showPoweredBy}
							onPoweredByClick={onPoweredByClick}
							calculateHeight={slideHeight}
							onRefReady={handleChildRefReady}
							themeColor={themeColor}
							wrapper={wrapper}
							containerSize={containerSize}
							floElements={floElements}
							steps={steps}
						/>
					)}
				</ul>
			</div>
		</Grid>
	);
};

export default SlideShow;
